import {
  PROJECT_COLOR_MENU_ID,
  ProjectColorMenu,
  ToolbarAnchor,
} from '@components/ArtBoard/ArtboardToolbar'
import { getCanvasState } from '@components/ArtBoard/Canvas/helpers/canvas'
import type { ID, NodeVariant } from '@octopus-do/octopus-project-core'
import { Toolbar } from '@octopus-do/octopus-storybook'
import {
  BlockAdd,
  Clone,
  Linktopage,
  Trash,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'

import { NODE_GHOST } from '@/constants/constants'
import { createSwitch } from '@/utils'
import {
  FRAME_MENU_ID,
  FrameMenu,
  GENERATE_POPOVER_ID,
  GeneratePopover,
  LINK_POPOVER_ID,
  LinkPopover,
  MORE_MENU_ID,
  MoreMenu,
  REMOVE_LINK_KEY,
  TagsMenu,
} from './components'
import { NODE_BORDER_WIDTH } from './constants'
import { useNodeToolbar } from './hooks'

interface Props {
  colorId: ID
  nodeId: ID
  // null on a page without a link
  url: null | string
  variant: NodeVariant
}

export const NodeActionsToolbar: React.FC<Props> = ({
  colorId,
  nodeId,
  url,
  variant,
}) => {
  const {
    actions,
    addBlockDisabled,
    arrowsDisabled,
    copyTreeDisabled,
    duplicateDisabled,
    frameLocked,
    generateDisabled,
    linkDisabled,
    pasteDisabled,
    pasting,
    generating,
    seoDisabled,
    tagsLocked,
  } = useNodeToolbar(nodeId)

  const { scale } = getCanvasState(
    document.getElementById('gestureMovableContainer')
  )

  const switcher = createSwitch()
    .case(['add_block'], actions.onAddBlock)
    .case([PROJECT_COLOR_MENU_ID], actions.onChangeColor)
    .case(['arrows'], actions.onArrows)
    // the menu reports FRAME_OPTIONS ids, which are NodeVariant values
    .case([FRAME_MENU_ID], (next) => actions.onChangeFrame(next as NodeVariant))
    .case([MORE_MENU_ID, 'seo'], actions.onSeo)
    .case([LINK_POPOVER_ID, REMOVE_LINK_KEY], actions.onRemoveLink)
    .case([LINK_POPOVER_ID], actions.onLink)
    .case([GENERATE_POPOVER_ID], actions.onGenerate)
    .case([MORE_MENU_ID, 'copy'], actions.onCopy)
    .case([MORE_MENU_ID, 'duplicate'], actions.onDuplicate)
    .case([MORE_MENU_ID, 'copy_tree'], actions.onCopyTree)
    .case([MORE_MENU_ID, 'paste'], actions.onPaste)
    .case(['duplicate'], actions.onDuplicate)
    .case(['delete'], actions.onDelete)

  const onClick = (keys: string[]) => switcher.run(keys)

  const ghost = variant === NODE_GHOST

  if (ghost) {
    return (
      <ToolbarAnchor scale={scale}>
        <Toolbar.Root onClick={onClick}>
          <FrameMenu
            locked={frameLocked}
            onUpgrade={actions.onUpgradeFrame}
            variant={variant}
          />
          <ProjectColorMenu colorId={colorId} />
          <Toolbar.Divider />
          <Toolbar.Button
            disabled={duplicateDisabled}
            icon={Clone}
            id="duplicate"
            tooltip="Duplicate"
          />
          <Toolbar.Button
            icon={Trash}
            id="delete"
            tooltip="Delete"
            variant="danger"
          />
        </Toolbar.Root>
      </ToolbarAnchor>
    )
  }

  return (
    <ToolbarAnchor borderWidth={NODE_BORDER_WIDTH} scale={scale}>
      <Toolbar.Root onClick={onClick}>
        <Toolbar.Button
          disabled={addBlockDisabled}
          icon={BlockAdd}
          id="add_block"
          tooltip="Add block"
        />
        <FrameMenu
          locked={frameLocked}
          onUpgrade={actions.onUpgradeFrame}
          variant={variant}
        />
        <ProjectColorMenu colorId={colorId} />
        <TagsMenu
          locked={tagsLocked}
          nodeId={nodeId}
          onUpgrade={actions.onUpgradeTags}
        />
        <LinkPopover disabled={linkDisabled} nodeId={nodeId} url={url ?? ''} />
        <Toolbar.Button
          disabled={arrowsDisabled}
          icon={Linktopage}
          id="arrows"
          tooltip="Arrows"
        />
        <Toolbar.Divider />
        <GeneratePopover disabled={generateDisabled} loading={generating} />
        <Toolbar.Divider />
        <MoreMenu
          copyTreeDisabled={copyTreeDisabled}
          duplicateDisabled={duplicateDisabled}
          pasteDisabled={pasteDisabled}
          pasting={pasting}
          seoDisabled={seoDisabled}
        />
        <Toolbar.Button
          icon={Trash}
          id="delete"
          tooltip="Delete"
          variant="danger"
        />
      </Toolbar.Root>
    </ToolbarAnchor>
  )
}
